<template>
    <span
        :class="[classObject, !color ? classObjectTheme : '']"
        :style="[color ? styleObject : '']"
        data-spec-class="badge"
    >
        <slot />
    </span>
</template>

<script lang="ts" setup>
import { computed, defineProps, withDefaults } from 'vue';
import { BadgeSize, BadgeTheme } from '@/types';
import { contrastColor } from '@/utils/colorUtils';

interface Props {
    theme?: BadgeTheme;
    size?: BadgeSize;
    isFloating?: boolean;
    color?: string;
}

const props = withDefaults(defineProps<Props>(), {
    theme: BadgeTheme.Primary,
    size: BadgeSize.Normal,
    isFloating: false,
    color: undefined,
});

const classObject = computed(() => ({
    'rounded-full font-medium whitespace-nowrap items-center': true,
    'inline-flex': !props.isFloating,
    'absolute flex justify-center shadow-lg top-0 right-0 translate-x-1/2 -translate-y-1/2':
        props.isFloating,

    'px-1.5 py-0.5 text-2xs': props.size === BadgeSize.Small,
    'px-2 py-0.5 text-xs': props.size === BadgeSize.Normal,
    'px-3 py-0.5 text-base': props.size === BadgeSize.Large,
}));

const classObjectTheme = computed(() => ({
    'bg-secondary--inverse text-primary': props.theme === BadgeTheme.Secondary,
    'bg-danger text-gray-100 dark:text-gray-100': props.theme === BadgeTheme.Danger,
    'bg-cta text-gray-100 dark:text-gray-100': props.theme === BadgeTheme.Cta,
    'bg-warning text-gray-900 dark:text-gray-900': props.theme === BadgeTheme.Warning,
    'bg-success text-gray-900 dark:text-gray-900': props.theme === BadgeTheme.Success,
    'bg-blue-800 text-gray-100': props.theme === BadgeTheme.Primary,
}));

const styleObject = computed(() => {
    return { backgroundColor: props.color, color: contrastColor(props.color) };
});
</script>
